"use client";

import { useContext, useMemo, useRef, useState, useEffect } from "react";
import styles from './_video-library.module.scss';
import Video from '@/components/video';
import CustomImage from "@/components/image";
import { LayoutContext } from "@/components/layout-context-container";
import { useRouter } from "next/router";
import video from "@/components/video";
import getOptimizedImageUrl from "@/util/getOptimizedImageUrl";

const VideoListItem = ({ video, isActive, onClick, videoPlaying = false }) => (
  <div
    className={`${styles.video_list_item} ${isActive && videoPlaying ? styles.active : ''}`}
    onClick={onClick}
    data-gtm-event-category="Action"
    data-gtm-event-action="Click"
    data-gtm-event-label={video.label? video.label : `Select ${video.title} Video`}
    role="button"
    tabIndex={-1}
  >
    <div className={styles.video_list_img}> 
      <CustomImage
        src={video.thumbnailSrc}
        width={160}
        height={90}
        alt={video.title}
      />
      <div className={styles.video_list_status}>
        {isActive && videoPlaying ? <span>NOW PLAYING</span> : <span className={styles.play_icon}></span>}
      </div>
    </div>
    <div className={styles.video_list_text}> 
      <p className={styles.video_list_pretitle}>{video.preTitle}</p>
      <p className={styles.video_list_title}>{video.title}</p>
    </div>
  </div>
);

export default function HcpVideoLibrary({ sectionTitle, videoData, videoId, disclaimer = null, thumbAlt = null }) {
  const { setAutoScrollActive } = useContext(LayoutContext);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [autoplaying, setAutoplaying] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [userInitiated, setUserInitiated] = useState(false);
  const currentVideo = useMemo(() => videoData[currentVideoIndex], [videoData, currentVideoIndex]);

  // Handle anchor navigation to specific videos
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && videoData) {
        const matchingVideoIndex = videoData.findIndex(video => video.anchor === hash);
        if (matchingVideoIndex !== -1 && matchingVideoIndex !== currentVideoIndex) {
          setAutoScrollActive(true);
          setTimeout(() => {
            setCurrentVideoIndex(matchingVideoIndex);
            videoRef.current?.stop();

            setTimeout(() => {
              videoRef.current?.rewind();
              videoRef.current?.autoplay();
            }, 50);
          }, 50); 
        }
      }
    };

    handleHashChange();
    // window.addEventListener('hashchange', handleHashChange);

    // return () => window.removeEventListener('hashchange', handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLibraryChoice = (index, anchor) => {
    setUserInitiated(true); 
    const newUrl = encodeURI(`${window.location.origin}${window.location.pathname}${anchor ? `#${anchor}` : ''}`);
    window.history.replaceState(null, '', newUrl);

    if (index === currentVideoIndex && !autoplaying && !videoPlaying) {
      videoRef.current?.play();
      return;
    }
    if (videoRef.current && (autoplaying || videoRef.current.playing)) {
      videoRef.current?.stop();

      if (index === currentVideoIndex && !autoplaying && videoPlaying) {
        setAutoScrollActive(true);

        setTimeout(() => {
          if (window.innerWidth < 1024) {
            const y = containerRef.current?.getBoundingClientRect().top + window.scrollY - 40;
            window.scrollTo({top: y, behavior: "smooth"});
          }
        }, 600);
        return;
      }
    }

    setAutoScrollActive(true);
    setAutoplaying(false);
    setCurrentVideoIndex(index);

    setTimeout(() => {
      if (window.innerWidth < 1024) {
        const y = containerRef.current?.getBoundingClientRect().top + window.scrollY - 40;
        window.scrollTo({top: y, behavior: "smooth"});
      }
      (index !== currentVideoIndex || autoplaying) && videoRef.current.rewind();
      videoRef.current?.play();
    }, 600);
  };

  return (
    <section className={styles.container} ref={containerRef}>
      {videoData.map((video, index) => (
        video.anchor && <div key={index} id={video.anchor}></div>
      ))}
      <div className={`container ${styles.content}`}>
        {sectionTitle && (
          <h2 className={`h3 ${styles.section_title}`}>{sectionTitle}</h2>
        )}

        <div className={styles.video_wrapper}>
          <div className={styles.video_section}>
            <Video
              ref={videoRef}
              url={null}
              urlSrc={videoData[currentVideoIndex].videoURL}
              title={videoData[currentVideoIndex].title}
              poster={getOptimizedImageUrl(videoData[currentVideoIndex].thumbnailSrc, 1464)}
              id={videoId}
              caption={videoData[currentVideoIndex].caption}
              thumbAlt={thumbAlt || ''}
              autoplay={true}
              onVideoPause={() => setVideoPlaying(false)}
              onVideoPlay={() => {
                videoRef.current.playing && setUserInitiated(true);
                setVideoPlaying(true);
              }}
              gtmEventCategory="Video"
              gtmEventAction="Start"
              gtmEventLabel={`${videoData[currentVideoIndex].title} Play Button`}
            />
            <div className={styles.video_text}>
              <p className={styles.video_title}>{currentVideo.title}</p>
              <p className={styles.video_description}>{currentVideo.description}</p>
            </div>
          </div>
          <div className={styles.video_scroll_list_wrapper}>
            <div className={styles.video_scroll_list}>
              {videoData.map((video, index) => (
                <VideoListItem
                  key={index}
                  video={video}
                  isActive={currentVideoIndex === index}
                  onClick={() => handleLibraryChoice(index, video.anchor)}
                  videoPlaying={userInitiated && videoPlaying}
                />
              ))}
            </div>
            {disclaimer && 
              <p className="text-sm text-hcp-charcoal mt-4 tablet:ml-4">
                {disclaimer}
              </p>
            }
          </div>
        </div>
      </div>
    </section>
  );
}
